import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { RollMaterial } from "../models/RollMaterial";
import { useTableData } from "../hooks/useTableData";

interface RollMaterialsTableProps {
	rollMaterials: RollMaterial[];
	selectedMaterial: RollMaterial | null;
	onRowSelected: (material: RollMaterial | null) => void;
	onDelete: (material: RollMaterial) => void;
}

export const RollMaterialsTable = ({
	rollMaterials,
	selectedMaterial,
	onRowSelected,
	onDelete,
}: RollMaterialsTableProps) => {
	const gridRef = useRef<AgGridReact<RollMaterial>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<RollMaterial>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<RollMaterial>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowId = useCallback((params: { data: RollMaterial }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<RollMaterial>
			ref={gridRef}
			rowData={rollMaterials}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedMaterial?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
